import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Mail, CheckCircle2, AlertCircle, FileText, ShieldCheck, Trash2, UserX } from 'lucide-react';
import SEO from '../components/SEO';
import Container from '../components/ui/Container';
import Button from '../components/ui/Button';
import { supportConfig } from '../data/supportConfig';
import { fetchLegalDocList, urlSegmentFromDocKey } from '../services/airtableService';
import { sendContactInquiry } from '../services/contactService';
import './Support.css';

const Support = () => {
  const { project } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const config = supportConfig[project];
  // legal_policies.app 값: 기본은 URL slug, slug와 다르면 config.legalApp 으로 override
  const legalApp = config?.legalApp || project;

  // 이 프로젝트의 활성 법적 문서 목록 (약관·정책 섹션에 전부 노출)
  const [legalDocs, setLegalDocs] = useState([]);
  useEffect(() => {
    let alive = true;
    fetchLegalDocList(legalApp).then((docs) => {
      if (alive) setLegalDocs(docs);
    });
    return () => {
      alive = false;
    };
  }, [legalApp]);

  // 앱에서 진입할 때 로그인 계정 식별자를 쿼리로 전달한다.
  //   /support/meetrip?uid=<userId>&email=<email>
  // 웹에서 직접 접속하면 값이 없으므로 고객 ID 없이 접수된다.
  const customerId = searchParams.get('uid') || '';
  const prefillEmail = searchParams.get('email') || '';

  useEffect(() => {
    // #delete-data / #delete-account 등 해시가 있으면 해당 섹션으로 스크롤, 없으면 최상단.
    const hash = location.hash ? location.hash.slice(1) : '';
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'instant', block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.hash]);

  const [form, setForm] = useState({
    email: '',
    category: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  // 쿼리로 넘어온 이메일 프리필 (config 로딩 이후 1회)
  useEffect(() => {
    if (prefillEmail) {
      setForm((prev) => ({ ...prev, email: prefillEmail }));
    }
  }, [prefillEmail]);

  const categories = useMemo(
    () => config?.inquiryCategories ?? ['일반 문의', '기타'],
    [config]
  );

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // 간단 유효성 (이메일 형식 + 내용 10자 이상)
    const emailOk = /.+@.+\..+/.test(form.email.trim());
    if (!emailOk || form.message.trim().length < 10) {
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus(null);

    try {
      await sendContactInquiry({
        source: config.webhookSource,
        service: config.serviceName,
        page: 'support',
        email: form.email.trim(),
        category: form.category || categories[0],
        content: form.message.trim(),
        customerId: customerId || null,
      });

      setStatus('success');
      setForm({ email: prefillEmail || '', category: '', message: '' });
    } catch (err) {
      console.error('Support form error:', err);
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  // 등록되지 않은 프로젝트 slug — 안내 후 홈 링크
  if (!config) {
    return (
      <div className="support-page">
        <SEO title="고객센터" description="Flowto.AI 고객센터" noindex />
        <Container>
          <div className="support-empty">
            <h1>준비 중인 고객센터예요</h1>
            <p>요청하신 고객센터 페이지를 찾을 수 없습니다.</p>
            <Link to="/" className="support-empty__home">Flowto.AI 홈으로</Link>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="support-page">
      <SEO
        title={`${config.serviceNameKo} 고객센터`}
        description={`${config.serviceNameKo}(${config.serviceName}) 고객센터 — 자주 묻는 질문, 신고·안전, 계정·데이터 문의`}
        url={`/support/${project}`}
      />

      {/* Hero */}
      <header className="support-hero">
        <Container>
          <span className="support-hero__eyebrow">Customer Center</span>
          <h1 className="support-hero__title">도와드릴게요</h1>
          <p className="support-hero__sub">{config.heroSubtitle}</p>
          <p className="support-hero__service">
            {config.serviceNameKo} ({config.serviceName})
          </p>
        </Container>
      </header>

      <Container>
        <div className="support-body">
          {/* FAQ */}
          <section className="support-section" aria-labelledby="support-faq-title">
            <span className="support-section__eyebrow">FAQ</span>
            <h2 id="support-faq-title" className="support-section__title">
              자주 묻는 질문
            </h2>
            <div className="support-faq">
              {config.faq.map((item, i) => (
                <details className="support-faq__item" key={i}>
                  <summary className="support-faq__q">{item.q}</summary>
                  <p className="support-faq__a">{item.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* Data & account deletion (Google Play 데이터 삭제 요건) */}
          {config.dataDeletion && (
            <section
              id="data-deletion"
              className="support-section"
              aria-labelledby="support-deletion-title"
            >
              <span className="support-section__eyebrow">Data &amp; Account Deletion</span>
              <h2 id="support-deletion-title" className="support-section__title">
                데이터 및 계정 삭제 요청
              </h2>
              <p className="support-section__desc">
                {config.serviceNameKo}({config.serviceName}) · 개발자 {config.dataDeletion.developerName} —
                아래 절차로 데이터 또는 계정의 삭제를 요청하실 수 있습니다.
              </p>

              <div id="delete-account" className="support-delete">
                <h3 className="support-delete__title">
                  <UserX size={18} /> 계정 및 관련 데이터 삭제
                </h3>
                <p className="support-delete__lead">
                  {config.serviceNameKo}({config.serviceName}) 계정과 그에 연결된 데이터를 삭제하려면:
                </p>
                <ol className="support-delete__steps">
                  {config.dataDeletion.account.steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
                <dl className="support-delete__meta">
                  <div><dt>삭제되는 데이터</dt><dd>{config.dataDeletion.account.deleted}</dd></div>
                  <div><dt>보관되는 데이터</dt><dd>{config.dataDeletion.account.retained}</dd></div>
                  <div><dt>보관 기간</dt><dd>{config.dataDeletion.account.retentionPeriod}</dd></div>
                </dl>
              </div>

              <div id="delete-data" className="support-delete">
                <h3 className="support-delete__title">
                  <Trash2 size={18} /> 특정 데이터 삭제 (계정 유지)
                </h3>
                <p className="support-delete__lead">
                  계정은 유지하고 특정 데이터만 삭제하려면:
                </p>
                <ol className="support-delete__steps">
                  {config.dataDeletion.data.steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
                <dl className="support-delete__meta">
                  <div><dt>삭제되는 데이터</dt><dd>{config.dataDeletion.data.deleted}</dd></div>
                  <div><dt>보관되는 데이터</dt><dd>{config.dataDeletion.data.retained}</dd></div>
                  <div><dt>보관 기간</dt><dd>{config.dataDeletion.data.retentionPeriod}</dd></div>
                </dl>
              </div>
            </section>
          )}

          {/* Contact form */}
          <section className="support-section" aria-labelledby="support-contact-title">
            <span className="support-section__eyebrow">Contact</span>
            <h2 id="support-contact-title" className="support-section__title">
              문의 보내기
            </h2>
            <p className="support-section__desc">
              위에서 해결되지 않은 내용은 아래로 보내 주세요. 답변은 입력하신 이메일로 드립니다.
            </p>

            <form className="support-form" onSubmit={onSubmit} noValidate>
              <label className="support-form__field">
                <span>이메일<em> *</em></span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="답변을 받으실 주소"
                  required
                />
              </label>

              {customerId && (
                <label className="support-form__field">
                  <span>고객 ID</span>
                  <input
                    type="text"
                    value={customerId}
                    readOnly
                    className="support-form__readonly"
                  />
                  <small className="support-form__hint">
                    앱에서 접속해 자동으로 입력되었습니다.
                  </small>
                </label>
              )}

              <label className="support-form__field">
                <span>문의 분류</span>
                <select name="category" value={form.category} onChange={onChange}>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>

              <label className="support-form__field">
                <span>문의 내용<em> *</em></span>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={6}
                  placeholder="문의하실 내용을 자세히 적어 주세요. (최소 10자)"
                  required
                />
              </label>

              {status === 'success' && (
                <div className="support-form__alert support-form__alert--success">
                  <CheckCircle2 size={18} />
                  문의가 접수되었습니다. 영업일 기준 1~2일 내에 답변드릴게요.
                </div>
              )}
              {status === 'error' && (
                <div className="support-form__alert support-form__alert--error">
                  <AlertCircle size={18} />
                  이메일 형식과 문의 내용(10자 이상)을 확인해 주세요. 문제가 계속되면 {config.contactEmail} 으로 보내주세요.
                </div>
              )}

              <div className="support-form__footerRow">
                <a className="support-form__mail" href={`mailto:${config.contactEmail}`}>
                  <Mail size={16} /> {config.contactEmail}
                </a>
                <Button type="submit" variant="primary" size="lg" pill disabled={submitting}>
                  {submitting ? '보내는 중…' : '보내기'}
                </Button>
              </div>
            </form>
          </section>

          {/* Policies */}
          <section className="support-section" aria-labelledby="support-policy-title">
            <span className="support-section__eyebrow">Policies</span>
            <h2 id="support-policy-title" className="support-section__title">
              약관 · 정책
            </h2>
            <ul className="support-policies">
              {legalDocs.length > 0 ? (
                legalDocs.map((d) => (
                  <li key={d.doc_key}>
                    <Link to={`/support/${project}/${urlSegmentFromDocKey(d.doc_key)}`}>
                      <FileText size={16} /> {d.name}
                    </Link>
                  </li>
                ))
              ) : (
                <>
                  <li>
                    <a href={config.termsUrl} target="_blank" rel="noreferrer">
                      <FileText size={16} /> 이용약관
                    </a>
                  </li>
                  <li>
                    <a href={config.privacyUrl} target="_blank" rel="noreferrer">
                      <ShieldCheck size={16} /> 개인정보처리방침
                    </a>
                  </li>
                </>
              )}
              {(config.extraPolicies ?? []).map((p) => (
                <li key={p.url}>
                  <a href={p.url} target="_blank" rel="noreferrer">
                    <ShieldCheck size={16} /> {p.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </Container>
    </div>
  );
};

export default Support;
